import { Button, DialogTrigger, Switch } from '@ibsys/kit';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserResponse, useUsers } from '@/entities/users';
import { useDebouncedSearch } from '@/shared/hooks';
import { getOutsideTotal } from '@/shared/lib';
import { SelectionDialog, useLocalSelectionItems, useSelectionDialogState } from '@/shared/ui';
import type { UserGroupMemberValue } from '../UsersGroupForm/types';
import { UserGroupMemberTitle } from './UserGroupMemberTitle';

/** Сколько пользователей показывать в окне: список набирается поиском по всему набору, а не листается */
const USERS_PAGE_LIMIT = 50;

const getUserId = (user: UserResponse) => user.id;
const getUserEmail = (user: UserResponse) => user.email;
const renderUserTitle = (user: UserResponse) => <UserGroupMemberTitle user={user} />;

type Props = {
  /** Пользователи, уже состоящие в группе */
  existingUsers: UserGroupMemberValue[];
  onSave: (users: UserResponse[]) => void;
};

/** Кнопка «Добавить пользователя» и окно выбора пользователей для группы */
export const AddUsersDialog: FC<Props> = ({ existingUsers, onSave }) => {
  const { t } = useTranslation(['common', 'users']);

  // По умолчанию показываем только активных пользователей
  const [showOnlyActive, setShowOnlyActive] = useState(true);
  const { isOpen, search, setSearch, selected, handleOpenChange, handleToggle } = useSelectionDialogState({
    getItemId: getUserId,
  });
  const appliedSearch = useDebouncedSearch(search);

  // Поиск по ФИО, роли и почте идёт по всему набору: пользователь за пределами первой
  // порции иначе недостижим
  const { data, isLoading } = useUsers({
    search: appliedSearch || undefined,
    limit: USERS_PAGE_LIMIT,
    isActive: showOnlyActive || undefined,
  });
  const loadedUsers = data?.users ?? [];

  const items = useLocalSelectionItems({
    items: loadedUsers,
    getItemId: getUserId,
    excludedIds: existingUsers.map((user) => user.id),
  });

  const handleConfirm = () => {
    onSave(selected);
    handleOpenChange(false);
  };

  return (
    <SelectionDialog<UserResponse>
      open={isOpen}
      onOpenChange={handleOpenChange}
      trigger={
        <DialogTrigger asChild>
          <Button variant="secondary" size="sm" className="ml-auto">
            {t('users:addUser')}
          </Button>
        </DialogTrigger>
      }
      title={t('users:addUser')}
      search={search}
      onSearchChange={setSearch}
      filters={<Switch value={showOnlyActive} onChange={setShowOnlyActive} label={t('common:showActive')} />}
      items={items}
      // Участники группы за выдачей окну неизвестны: из общего числа вычитаются только попавшие в неё
      total={getOutsideTotal(data?.total, loadedUsers.length, loadedUsers.length - items.length)}
      isLoading={isLoading}
      selected={selected}
      onToggle={handleToggle}
      getItemId={getUserId}
      renderTitle={renderUserTitle}
      renderHint={getUserEmail}
      labels={{ empty: t('common:noItemsToSelect'), notFound: t('users:notFound'), confirm: t('common:add') }}
      onConfirm={handleConfirm}
    />
  );
};
